import { writable, derived, get } from 'svelte/store'

export let chatMessages = writable({unread: 0, msgs: [], windowOpen: false});

// -- Platform permission verdicts ("prompt" | "granted" | "denied"). The single copy of
// -- this state, with a single writer: Devices._recordPermission. UI gates on === "granted".
export const device_permissions = writable({ camera: "prompt", microphone: "prompt" });

// -- Devices
export const avail_devices = writable({
    camera: [],
    microphone: [],
    speaker: [],
});

// -- each kind's track/muted are stable stores created exactly once (setSelectedDevice never
// -- replaces them), so views -- and the derived readiness below -- can subscribe directly
// -- and survive every selection update
const _track = {
    microphone: writable(null),
    camera: writable(null),
};
const _muted = {
    microphone: writable(false),
    camera: writable(false),
    speaker: writable(false),
    stream: writable(false),
};

export const selected_devices = writable({
    microphone: {label: null, id: null, track: _track.microphone, muted: _muted.microphone},
    camera: {label: null, id: null, track: _track.camera, muted: _muted.camera},
    speaker: {label: null, id: null, track: null, muted: _muted.speaker},
    stream: {label: null, id: null, track: null, muted: _muted.stream}, // -- the device list for this is the same as speaker, though the selection may be different.
});

// -- Readiness, derived: a camera is "ready" when the selection holds a track and isn't
// -- muted. Nothing sets this -- it follows the desired/actual state. (mic_ready is gone;
// -- it was never set true anywhere.)
export const camera_ready = derived(
    [_track.camera, _muted.camera],
    ([$track, $muted]) => Boolean($track) && !$muted
);

// -- Devices Getters
export function getSelectedDevice(type) {
    return get(selected_devices)?.[type] ?? null;
}

export function getSelectedDeviceTrack(type) {
    const device = getSelectedDevice(type);
    return device?.track ? get(device.track) : null;
}

export function getSelectedDeviceMuted(type) {
    const device = getSelectedDevice(type);
    return device?.muted ? get(device.muted) : false;
}


// -- Subscriptions
export function subscribeSelectedDeviceTrack(type, callback) {
    return getSelectedDevice(type)?.track?.subscribe(callback) ?? (() => {});
}

export function subscribeSelectedDeviceMuted(type, callback) {
    return getSelectedDevice(type)?.muted?.subscribe(callback) ?? (() => {});
}

export function subscribeSelectedDeviceSelection(type, callback) {
    let lastLabel = Symbol("unset");
    let lastId = Symbol("unset");

    return selected_devices.subscribe(current => {
        const device = current[type];
        const label = device?.label ?? null;
        const id = device?.id ?? null;

        if (label === lastLabel && id === lastId)
            return;

        lastLabel = label;
        lastId = id;
        callback(device ? { label, id } : null);
    });
}


// -- Setter
export function setSelectedDevice(type, updates = {}) {
    selected_devices.update(current => {
        
        const device = current[type];
        if (!device) return current;

        const nextDevice = {...device};

        if (Object.hasOwn(updates, "label"))
            nextDevice.label = updates.label;

        if (Object.hasOwn(updates, "id"))
            nextDevice.id = updates.id;

        if (Object.hasOwn(updates, "track") && device.track)
            device.track.set(updates.track);

        if (Object.hasOwn(updates, "muted"))
            device.muted.set(Boolean(updates.muted));

        return {
            ...current,
            [type]: {
                ...nextDevice,
                track: device.track,
                muted: device.muted
            }
        };
    });
}



// -- Loading
// -- the jitsi connection is up and the conference object exists (handlers registered, ready to
// -- accept local tracks). This -- not CONFERENCE_JOINED -- gates the Join button: the conference
// -- is only joined from the Join click, after the click publishes the local tracks onto it.
export const conf_conn_ok = writable(false);
export const core_init_ok = writable(false);
// -- core_init_ok will never come: the decoder's first boot threw (core-init-failed) or never
// -- landed (StartPanelv2's join watchdog). The start panel shows a reload notice instead of waiting
export const core_init_failed = writable(false);
export const decoder_thread_ready = writable(false);
export const playback_ctx_resume_ok = writable(false);
export const dts_ctx_resume_ok = writable(false);

// -- derived by MediaLifecycle: on iOS the audio session is gone (a foreground call/Siri
// -- interruption, or another app taking it) and getting it back wants a user gesture. Drives
// -- the tap-to-resume overlay. Derived, not latched -- an interruption that ends on its own
// -- clears it without a tap.
export const needs_user_resume = writable(false);

// -- MediaLifecycle's two derived media-lifecycle levels (see the table there).
// --
// -- video_suspended: an ACTION, iOS only -- the page is backgrounded or the audio session was
// -- interrupted, so stop drawing. Drives the main-thread RAFs (conference camera pump in
// -- tile.svelte, level meters in AudioMeter.svelte) alongside the worker's decoder RAF. Needed
// -- because keeping the page alive for audio defeats the browser's automatic background RAF
// -- throttling, so these would otherwise keep drawing invisibly.
// --
// -- audio_suspended: an OBSERVATION -- the OS took the audio session away (we never suspend
// -- audio ourselves). Its FALLING edge is the point: iOS pauses every media element during an
// -- interruption and only un-pauses them when it supplies the ShouldResume hint, and our only
// -- other play() is at attach time -- so without this the tiles are left holding paused
// -- elements. For the pump <video> that means a live RAF whose frame counter never advances:
// -- pump() early-returns and the canvas keeps its last frame (frozen, not dark).
export const video_suspended = writable(false);
export const audio_suspended = writable(false);

// -- worker-reported connectivity of the two base connections: the MOQ relay and the pl-ws
// -- signaling server (Jitsi self-heals separately). Drives the connection-lost banner. Defaults
// -- to connected so it doesn't flash before the first connect; only real drops/failures show.
// -- moq_in_use is false when MOQ fell back to ws at connect time, so a "down" MOQ isn't flagged.
export const connection_status = writable({ ws: true, moq: true, moq_in_use: false, reconnecting: false, gave_up: false });

// -- Session/Transport States
export const playback = writable(false);

// -- set by the decoder thread (DecoderPeer) when the eng video path is stalled dropping delta
// -- frames while it waits for a keyframe to resync -- e.g. a viewer that subscribed mid-GOP, so
// -- audio plays immediately but video can't start until the next keyframe. Drives the loader
// -- overlay on the playback canvas.
export const awaiting_keyframe = writable(false);

// -- true once the user completes their first Join. Drives post-join audio: conference tiles
// -- unmute and the awp stream fades up. Monotonic -- returning to the start panel via settings
// -- keeps audio live and honors mute choices instead of re-gating.
export const has_joined = writable(false);

// Add this new store:
export const pluginUpdateAvailable = writable(false);

// -- pl-ws refused this eng tab: another eng tab is connected to the room (see
// -- Dispatcher eng-conflict). Drives the blocking DuplicateEngAlert overlay.
export const duplicateEngAlert = writable(false);

// -- ABR State
export const abr_state = writable("HI");

// -- UI States
export const two_col_mode = writable(false);
export const start_panel_open = writable(true);  // -- the session (and its toolbar) is covered while true
export const landscape_mode = writable(false);
